import { motion } from "framer-motion";
import * as React from "react";
import { cn } from "~/utils";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  type: "password" | "search" | "text";
  preventAutofill?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, preventAutofill, autoComplete, ...props }, ref) => {
    // Override autoComplete when preventing autofill.
    // Use "new-password" in the visible field, and include hidden dummy trap fields.
    const computedAutoComplete = preventAutofill ? "new-password" : autoComplete;

    return (
      <>
        {preventAutofill && (
          // Hidden dummy fields that capture autofill and trap password manager heuristics.
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "-10000px",
              top: "-10000px",
              width: "1px",
              height: "1px",
              overflow: "hidden",
            }}
          >
            <input type="text" tabIndex={-1} autoComplete="username" />
            <input type="password" tabIndex={-1} autoComplete="new-password" />
          </div>
        )}
        <input
          type={type}
          autoComplete={computedAutoComplete}
          className={cn(
            "border-input placeholder:text-secondaryText text-foreground bg-background",
            "disabled:text-secondaryText/50 disabled:bg-background/50 disabled:border-muted/50",
            "flex h-10 w-full rounded-full border px-3 py-2",
            "font-sans text-sm font-normal tracking-normal focus-visible:outline-none",
            className,
          )}
          ref={ref}
          {...props}
        />
      </>
    );
  },
);
Input.displayName = "Input";

const MotionInput = motion(Input);

export { Input, MotionInput };
